import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, MessageSquarePlus, Trash2, X, Lock, ExternalLink } from "lucide-react";
import DemoLayout from "@/components/DemoLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "sent", label: "Sent", color: "hsl(var(--muted-foreground))", hint: "Submitted, waiting to hear back." },
  { id: "interviewing", label: "Interviewing", color: "hsl(var(--accent-amber))", hint: "In conversation - phone screen, on-site, take-home." },
  { id: "offer", label: "Offer", color: "hsl(var(--accent-green))", hint: "Verbal or written offer in hand." },
  { id: "closed", label: "Closed", color: "hsl(var(--primary))", hint: "Rejected, withdrew, or ghosted." },
];

const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]));

// Hand-crafted fake apps per persona. Each persona has at least one card
// in every kanban stage so the tracker page is illustrative end-to-end.
const FAKE_APPS = {
  diego: [
    {
      id: "diego-1",
      job: { title: "Motion Designer, Brand", company: "Canopy", location: "Brooklyn, NY (Hybrid)" },
      status: "sent",
      sent_at: daysAgo(2),
      updated_at: daysAgo(2),
      notes: "",
    },
    {
      id: "diego-2",
      job: { title: "Editorial Illustrator (Contract)", company: "Mantis Magazine", location: "Remote" },
      status: "sent",
      sent_at: daysAgo(5),
      updated_at: daysAgo(5),
      notes: "Applied via their submissions form. Sent five-piece editorial sample.",
    },
    {
      id: "diego-3",
      job: { title: "Senior Motion Designer", company: "Loop & Larkin", location: "Los Angeles (Remote-OK)" },
      status: "interviewing",
      sent_at: daysAgo(11),
      updated_at: daysAgo(2),
      notes: "Phone screen with Priya scheduled Thursday 11am ET. Reviewed three motion case studies.",
    },
    {
      id: "diego-4",
      job: { title: "Animation Lead, Streaming", company: "Sonora Sounds", location: "Mexico City (Hybrid)" },
      status: "offer",
      sent_at: daysAgo(24),
      updated_at: daysAgo(3),
      notes: "Verbal offer $94k + relocation stipend. Awaiting written package by Friday.",
    },
    {
      id: "diego-5",
      job: { title: "Motion Designer, Growth Team", company: "Pleat", location: "Remote (US)" },
      status: "closed",
      sent_at: daysAgo(31),
      updated_at: daysAgo(9),
      notes: "Passed on me. Recruiter said they went with a more senior in-house candidate.",
    },
    {
      id: "diego-6",
      job: { title: "Junior Art Director", company: "Glasshouse Studio", location: "Austin, TX" },
      status: "closed",
      sent_at: daysAgo(40),
      updated_at: daysAgo(14),
      notes: "",
    },
  ],
  sam: [
    {
      id: "sam-1",
      job: { title: "Senior Product Designer", company: "Linewave", location: "Remote (Americas)" },
      status: "sent",
      sent_at: daysAgo(1),
      updated_at: daysAgo(1),
      notes: "Submitted via Greenhouse. Tailored portfolio for B2B SaaS reporting work.",
    },
    {
      id: "sam-2",
      job: { title: "Lead UX Designer, Health", company: "Ember Health", location: "Boston (Hybrid)" },
      status: "sent",
      sent_at: daysAgo(4),
      updated_at: daysAgo(4),
      notes: "",
    },
    {
      id: "sam-3",
      job: { title: "Staff Product Designer", company: "Fieldwork Labs", location: "Remote" },
      status: "interviewing",
      sent_at: daysAgo(8),
      updated_at: daysAgo(1),
      notes: "Take-home: card-sorting + IA exercise. Due Monday.",
    },
    {
      id: "sam-4",
      job: { title: "Senior Designer, Patient Experience", company: "Kindling Care", location: "NYC (Hybrid)" },
      status: "interviewing",
      sent_at: daysAgo(14),
      updated_at: daysAgo(2),
      notes: "Round 2 with design director Rina, Friday 2pm. Will dig into the LMS IA case study.",
    },
    {
      id: "sam-5",
      job: { title: "Principal Product Designer", company: "Mosaic Tools", location: "Remote (US)" },
      status: "offer",
      sent_at: daysAgo(28),
      updated_at: daysAgo(4),
      notes: "Offer: $172k base + equity. Compete deadline: next Wednesday.",
    },
    {
      id: "sam-6",
      job: { title: "Design Lead, Mobile", company: "Birch & Co", location: "Chicago" },
      status: "closed",
      sent_at: daysAgo(35),
      updated_at: daysAgo(11),
      notes: "Withdrew. Wasn't excited about the team structure after the on-site.",
    },
  ],
};

function daysAgo(n) {
  return new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();
}

export default function DemoSent() {
  const [params] = useSearchParams();
  const personaId = params.get("persona");
  const [persona, setPersona] = useState(null);
  const [apps, setApps] = useState(() => FAKE_APPS[personaId] || FAKE_APPS.diego);

  useEffect(() => {
    if (!personaId) return;
    axios.get(`${API}/demo/profile/${personaId}`).then((r) => setPersona(r.data.profile)).catch(() => {});
    setApps(FAKE_APPS[personaId] || FAKE_APPS.diego);
  }, [personaId]);

  const groups = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const a of apps) {
      (out[a.status] || out.sent).push(a);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
    return out;
  }, [apps]);

  const moveStage = (id, nextStatus) => {
    setApps((curr) =>
      curr.map((a) => (a.id === id ? { ...a, status: nextStatus, updated_at: new Date().toISOString() } : a))
    );
    toast.success(`Moved to ${STAGE_BY_ID[nextStatus]?.label}.`);
  };

  const saveNotes = (id, notes) => {
    setApps((curr) => curr.map((a) => (a.id === id ? { ...a, notes, updated_at: new Date().toISOString() } : a)));
    toast.success("Saved.");
  };

  const deleteApp = (id) => {
    setApps((curr) => curr.filter((a) => a.id !== id));
    toast.success("Removed from tracker.");
  };

  return (
    <DemoLayout persona={persona}>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-10">
        <div className="terminal-label text-primary">[ APPLICATIONS.TRACKER ]</div>
        <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-2 mb-2">Sent</h1>
        <p className="text-sm text-muted-foreground mb-6 sm:mb-8 max-w-3xl">
          {apps.length} application{apps.length === 1 ? "" : "s"} tracked. Move cards between stages, edit notes, or remove a card. Demo mode keeps everything local; nothing leaves your browser.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <Column
              key={stage.id}
              stage={stage}
              cards={groups[stage.id] || []}
              onMove={moveStage}
              onSaveNotes={saveNotes}
              onDelete={deleteApp}
            />
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

function Column({ stage, cards, onMove, onSaveNotes, onDelete }) {
  return (
    <div className="panel p-0 flex flex-col min-h-[200px]" data-testid={`demo-kanban-col-${stage.id}`}>
      <div className="px-4 py-3 border-b border-foreground/20 flex items-center justify-between gap-2 sticky top-0 bg-background z-10">
        <div className="min-w-0">
          <div className="terminal-label" style={{ color: stage.color }}>[ {stage.label.toUpperCase()} ]</div>
          <div className="text-[10px] text-muted-foreground mt-1 truncate">{stage.hint}</div>
        </div>
        <span className="font-mono text-xs tabular-nums px-2 py-0.5 border border-foreground/20 bg-surface flex-shrink-0">{cards.length}</span>
      </div>
      <div className="p-2 space-y-2 flex-1">
        {cards.length === 0 && (
          <div className="px-3 py-6 text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono">- empty -</div>
        )}
        {cards.map((c) => (
          <KanbanCard key={c.id} entry={c} stage={stage} onMove={onMove} onSaveNotes={onSaveNotes} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function KanbanCard({ entry, stage, onMove, onSaveNotes, onDelete }) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(entry.notes || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const stampLabel = stage.id === "sent" ? "Sent" : "Updated";
  const stamp = entry.updated_at || entry.sent_at;

  const unavailable = () => toast.info("Unavailable in demo mode.");

  return (
    <div className="border border-foreground/15 bg-background hover:border-primary/40 transition-colors p-3 group relative" data-testid={`demo-kanban-card-${entry.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[13px] uppercase font-semibold tracking-tight leading-tight break-words" title={entry.job?.title}>{entry.job?.title}</div>
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">{entry.job?.company}{entry.job?.location ? ` · ${entry.job.location}` : ""}</div>
        </div>
        <button
          onClick={() => setConfirmDelete(true)}
          className="text-muted-foreground/60 hover:text-primary p-1 -m-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
          title="Remove from tracker"
          data-testid="demo-kanban-delete-trigger"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="mt-2 text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
        {stampLabel} {stamp ? new Date(stamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "-"}
      </div>

      {entry.notes && !editingNotes && (
        <p className="mt-2 text-[11px] text-foreground/75 leading-snug whitespace-pre-wrap break-words border-l-2 border-foreground/15 pl-2" data-testid="demo-kanban-notes">{entry.notes}</p>
      )}

      {editingNotes && (
        <div className="mt-2">
          <textarea
            data-testid="demo-kanban-notes-input"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="e.g. phone screen Mon 3pm with Lisa"
            className="input-base text-[11px] w-full"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => { setEditingNotes(false); onSaveNotes(entry.id, notesDraft); }}
              className="btn-primary text-[10px]"
              data-testid="demo-kanban-notes-save"
            >Save</button>
            <button
              onClick={() => { setEditingNotes(false); setNotesDraft(entry.notes || ""); }}
              className="btn-ghost text-[10px]"
            >Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-foreground/10 flex flex-wrap items-center gap-2 justify-between">
        <StatusDropdown current={stage.id} onChange={(next) => onMove(entry.id, next)} />
        <div className="flex items-center gap-1">
          {!editingNotes && (
            <button
              onClick={() => setEditingNotes(true)}
              className="text-muted-foreground hover:text-primary p-1"
              title={entry.notes ? "Edit note" : "Add a note"}
              data-testid="demo-kanban-notes-edit"
            ><MessageSquarePlus className="w-3.5 h-3.5" /></button>
          )}
          <button
            onClick={unavailable}
            className="text-muted-foreground/60 hover:text-primary p-1 inline-flex items-center"
            title="Unavailable in demo mode"
            data-testid="demo-kanban-portfolio-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={unavailable}
            className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted-foreground/70 hover:text-primary px-1.5 py-1 inline-flex items-center gap-1"
            title="Unavailable in demo mode"
            data-testid="demo-kanban-view"
          >
            <Lock className="w-2.5 h-2.5" /> View
          </button>
        </div>
      </div>

      {confirmDelete && (
        <DeleteConfirmModal
          job={entry.job}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => { setConfirmDelete(false); onDelete(entry.id); }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({ job, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      data-testid="demo-kanban-delete-modal"
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-md p-6">
        <button onClick={onCancel} aria-label="Close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
        <div className="terminal-label text-primary mb-3">[ REMOVE.APPLICATION ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-3">Remove this from your tracker?</h3>
        <p className="text-sm text-foreground/80 leading-relaxed mb-4">
          You&apos;re about to remove <span className="font-medium text-foreground">{job?.title}</span>{job?.company ? <> at <span className="font-medium text-foreground">{job.company}</span></> : null} from your kanban.
        </p>
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-6">
          In the real app, your tailored resume, cover letter, and portfolio for this role stay saved, and the job reappears in your feed. Only the tracker card and any notes you added here get cleared. This can&apos;t be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="btn-ghost text-[11px]" data-testid="demo-kanban-delete-cancel">Cancel</button>
          <button
            onClick={onConfirm}
            className="btn-primary inline-flex items-center gap-2 text-[11px]"
            data-testid="demo-kanban-delete-confirm"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusDropdown({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const here = STAGE_BY_ID[current];
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="demo-kanban-status-trigger"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-mono px-2 py-1 border border-foreground/15 hover:border-primary transition-colors"
        style={{ color: here?.color }}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: here?.color }} />
        {here?.label}
        <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-40 panel py-1 min-w-[160px] shadow-xl">
            {STAGES.map((s) => (
              <button
                key={s.id}
                onClick={() => { onChange(s.id); setOpen(false); }}
                data-testid={`demo-kanban-status-opt-${s.id}`}
                className={`w-full text-left px-3 py-2 text-[11px] hover:bg-surface-hover flex items-center gap-2 font-mono uppercase tracking-[0.18em] ${s.id === current ? "bg-surface" : ""}`}
                style={{ color: s.color }}
              >
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
